import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { pathFor, type Lang } from '../../../router/paths';

const images: { src: string; alt: string }[] = [
  { src: '/images/ambiente/Fruehstueck_Bio_2025.webp',                              alt: 'Bio-Frühstück im Hotel Alpenhof Gauting' },
  { src: '/images/ambiente/Kachelofenstube_Eckbank_2025.jpg',                       alt: 'Eckbank in der Kachelofenstube – Hotel Alpenhof Gauting' },
  { src: '/images/ambiente/Fruehstueck_innen_2025.jpg',                              alt: 'Frühstücksraum innen – Hotel Alpenhof Gauting' },
  { src: '/images/ambiente/Fruehstuecksraum_2025.jpg',                               alt: 'Frühstücksraum mit Tageslicht – Hotel Alpenhof Gauting' },
  { src: '/images/ambiente/Fruehstueck_Buffetraum_2025.jpg',                         alt: 'Frühstücksbuffet mit Bio-Produkten – Hotel Alpenhof Gauting' },
  { src: '/images/ambiente/Fruehstueck_Garten_2_2025.jpg',                           alt: 'Frühstück im Garten – Hotel Alpenhof Gauting im Sommer' },
  { src: '/images/ambiente/Kachelofenstube_Sitzecke_mit_Bibliothek_2025.jpg',        alt: 'Kachelofenstube mit Bibliothek – Hotel Alpenhof Gauting' },
];

export default function Breakfast() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'de';
  const [kiniOpen, setKiniOpen] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const prevImg = useCallback(() => {
    setImgIndex(i => (i - 1 + images.length) % images.length);
  }, []);

  const nextImg = useCallback(() => {
    setImgIndex(i => (i + 1) % images.length);
  }, []);

  // Bildslider: alle 5 Sekunden wechseln
  useEffect(() => {
    const timer = setInterval(() => {
      setImgIndex(i => (i + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const openKini = () => {
    setKiniOpen(true);
    if (!audioRef.current) {
      audioRef.current = new Audio('https://upload.wikimedia.org/wikipedia/commons/7/7e/Bayerischer_Defiliermarsch.ogg');
      audioRef.current.volume = 0.4;
    }
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  };

  const closeKini = () => {
    setKiniOpen(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // Escape-Taste schließt das Modal
  useEffect(() => {
    if (!kiniOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeKini();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [kiniOpen]);

  return (
    <section id="fruehstueck" className="py-12 sm:py-20 px-4 sm:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <div className="h-64 sm:h-96 lg:h-[600px] w-full relative overflow-hidden rounded-lg">
            {images.map((img, i) => (
              <img
                key={img.src}
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                style={{ opacity: imgIndex === i ? 1 : 0 }}
              />
            ))}
            {/* Arrow left */}
            <button
              onClick={prevImg}
              aria-label={t('sliderPrev')}
              className="absolute left-2 sm:left-5 top-1/2 -translate-y-1/2 text-white/85 hover:text-white transition-colors cursor-pointer z-10 sm:flex sm:items-center sm:justify-center sm:w-11 sm:h-11 sm:rounded-full sm:bg-black/30 sm:hover:bg-black/55 [filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.7))] sm:[filter:none]"
            >
              <i className="ri-arrow-left-s-line text-4xl sm:text-2xl leading-none" />
            </button>
            {/* Arrow right */}
            <button
              onClick={nextImg}
              aria-label={t('sliderNext')}
              className="absolute right-2 sm:right-5 top-1/2 -translate-y-1/2 text-white/85 hover:text-white transition-colors cursor-pointer z-10 sm:flex sm:items-center sm:justify-center sm:w-11 sm:h-11 sm:rounded-full sm:bg-black/30 sm:hover:bg-black/55 [filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.7))] sm:[filter:none]"
            >
              <i className="ri-arrow-right-s-line text-4xl sm:text-2xl leading-none" />
            </button>
            {/* Dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setImgIndex(i)}
                  aria-label={`Bild ${i + 1}`}
                  className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-all cursor-pointer ${
                    i === imgIndex ? 'bg-white scale-125' : 'bg-white/45 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] sm:text-xs font-semibold tracking-wider sm:tracking-widest uppercase text-amber-700 mb-3 sm:mb-4">{t('bfKicker')}</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-gray-800 mb-4 sm:mb-6">
              {t('bfTitlePart1')}{' '}
              <span
                className="relative inline-block cursor-pointer"
                onClick={openKini}
              >
                <span className="relative">
                  {t('bfTitleKini')}
                  <span className="absolute bottom-0 left-0 w-full h-[3px] bg-amber-600 rounded-full"></span>
                </span>
              </span>
              {' '}{t('bfTitlePart2')}
            </h2>

            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-4 sm:mb-5">
              {t('bfLead1')}
            </p>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8">
              {t('bfLead2')}
            </p>

            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                  <i className="ri-restaurant-line text-amber-700 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1">{t('bfFeat1Title')}</h3>
                  <p className="text-gray-600 text-sm">{t('bfFeat1Text')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                  <i className="ri-heart-line text-amber-700 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1">{t('bfFeat2Title')}</h3>
                  <p className="text-gray-600 text-sm">{t('bfFeat2Text')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                  <i className="ri-goblet-line text-amber-700 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1">{t('bfFeat3Title')}</h3>
                  <p className="text-gray-600 text-sm">{t('bfFeat3Text')}</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {kiniOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-4"
          onClick={closeKini}
          style={{ animation: 'kiniFadeIn 0.25s ease-out both' }}
        >
          {/* Mobile: Bottom-Sheet, Desktop: zentriertes Modal */}
          <div
            className="relative bg-white shadow-2xl overflow-hidden flex flex-col absolute inset-x-0 bottom-0 max-h-[90vh] rounded-t-2xl sm:static sm:inset-auto sm:max-h-none sm:rounded-2xl sm:max-w-3xl sm:w-full"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'kiniPopIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both' }}
          >
            <button
              onClick={closeKini}
              aria-label={t('close')}
              className="absolute top-3 right-3 z-10 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-gray-700 hover:text-gray-900 transition-colors shadow-md flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 overflow-y-auto">
              <div className="relative h-72 sm:h-80 md:h-auto flex-shrink-0 bg-stone-100">
                <img
                  src="/images/hotel/Koenig_Ludwig_II.webp"
                  width={800}
                  height={1200}
                  loading="lazy"
                  alt="König Ludwig II."
                  className="w-full h-full object-contain md:object-cover md:object-top"
                />
              </div>

              <div className="p-6 sm:p-8 flex flex-col">
                <p className="text-[10px] sm:text-xs font-semibold tracking-widest uppercase text-amber-700 mb-2">
                  {t('bfKiniKicker')}
                </p>
                <h3 className="text-2xl sm:text-3xl font-serif text-gray-800 mb-3">
                  {t('bfKiniTitle')}
                </h3>
                <p className="text-gray-500 text-sm mb-4">{t('bfKiniDates')}</p>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-3">
                  {t('bfKiniText1')}
                </p>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  {t('bfKiniText2')}
                </p>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes kiniFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes kiniPopIn {
              0% { opacity: 0; transform: translateY(40px); }
              100% { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </section>
  );
}
